
import Vue from "vue";
import { NolaAnalytics, NolaContentType, NolaItemId } from "@/lib/utils/analytics";
import { showNotifyDialog } from "@/lib/dialog";
import axiosBase from "axios";
import FadeFooterButton from "@/components/atoms/FadeFooterButton.vue";
import { User } from "@/lib/models";
import { optimizeImage } from "@/lib/utils/url";

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    FadeFooterButton,
  },
  async created() {
    const detailRequest = await axios.get(`/agent_feature_detail`);
    if (detailRequest.status !== 200) {
      await showNotifyDialog({
        title: "エラー",
        content: "ページの一部の情報の取得に失敗しました。",
      });
    }
    const [content] = detailRequest.data.contents;
    this.detail = content;
    this.isLoadCMS = true;

    // microCMSから取得したデータをフッターボタンに利用する為、
    // データ取得後ににフッターボタンを表示する
    this.isVisibleFooter = true;

    // microCMSから取得したデータが反映されてから監視する
    this.$nextTick(() => {
      const submitButton = this.$refs.submitButton as Element | undefined;
      if (submitButton) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // ボタンが画面に入った時にAnalyticsイベントを送信
              this.sendBottomImpressionAnalyticsEvent();
              observer.unobserve(entry.target);
            }
          });
        });
        observer.observe(submitButton);
      }
    });
  },
  data() {
    return {
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      detail: {
        partnerCompanyImage: { url: "" },
        cautions: "",
        linkedNovelCount: 0,
        ctaTopBtnText: "",
        ctaBottomBtnText: "",
        showLinkedNovelCount: false,
      },
      isVisibleFooter: false,
      isLoadCMS: false,
    };
  },
  props: {
    displayType: {
      type: String,
      default: "default",
      validator(value: string) {
        return ["intro", "campaign", "default"].includes(value);
      },
    },
  },
  computed: {
    isIntro() {
      return this.displayType === "intro";
    },
    isCampaign() {
      return this.displayType === "campaign";
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    isAgentFeatureNotAgreed() {
      return this.user?.agentStartedAt === null;
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    sendBottomImpressionAnalyticsEvent() {
      this.nolaAnalytics.logViewItemEvent(NolaItemId.AgentCtaBottom, NolaContentType.Button);
    },
    submit() {
      this.$store.dispatch("userModule/startNolaAgentFeature");
      this.nolaAnalytics.logButtonEvent(NolaItemId.StartAgentFeature);

      // Nolaの使いかたガイド画面から押下された場合、エージェント画面に遷移
      if (this.$route.path === "/commerce-debut-guide") {
        this.$router.push({ path: "/agent" });
      }
    },
    handleScroll() {
      if (!this.isIntro) return; // intro画面以外はボタンを非表示にしない為、処理終了

      // ウィンドウの高さが1500px以上の場合、固定値ではボタンが再表示されない可能性があるため、
      // ウィンドウの高さをfadePositionとして設定。それ以外の場合は、1500pxに固定
      const fadePosition = window.innerHeight >= 1500 ? window.innerHeight : 1500;

      // スクロール位置にウィンドウの高さを足した値をscrollPositionとする
      const scrollPosition = window.scrollY + window.innerHeight;

      // scrollPositionがfadePositionを超えたらボタンを非表示にし、それ以外は表示する
      if (scrollPosition > fadePosition) {
        this.isVisibleFooter = false;
      } else {
        this.isVisibleFooter = true;
      }
    },
    handleFooterAction() {
      if (!this.isIntro) {
        this.$router.push({ path: "/agent" });
      } else {
        const container = this.$refs.end as HTMLElement;
        container.scrollIntoView({ behavior: "smooth" });
      }
    },
    ctaTopClicked() {
      this.nolaAnalytics.logButtonEvent(NolaItemId.AgentCtaTop);
      this.handleFooterAction();
    },
    optimizedImage(url: string) {
      return optimizeImage(url);
    },
  },
});

interface Data {
  nolaAnalytics: NolaAnalytics;
  detail: {
    partnerCompanyImage: { url: string };
    cautions: string;
    linkedNovelCount: number;
    ctaTopBtnText: string;
    ctaBottomBtnText: string;
    showLinkedNovelCount: boolean;
  };
  isVisibleFooter: boolean;
  isLoadCMS: boolean;
}

interface Methods {
  sendBottomImpressionAnalyticsEvent(): void;
  submit(): void;
  handleScroll(): void;
  handleFooterAction(): void;
  ctaTopClicked(): void;
  optimizedImage(url: string): string;
}

interface Computed {
  isIntro: boolean;
  isCampaign: boolean;
  isAgentFeatureNotAgreed: boolean;
  user: User;
}

interface Props {
  displayType: string;
}
